import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { Breadcrumbs, Button, ListItemButton, TextField, Typography } from '@mui/material'

import { config } from '../config'
import {
  deletePaymentData,
  rejectPaymentData,
  approvePaymentAmountNovelty,
  approvePaymentReferenceNovelty,
  approveBackgroundResearchNovelty,
  approveBankAccountPaymentNovelty,
  approveIdentificationDocumentNovelty
} from '../api/payments.api'
import Dialog from '../components/common/dialog'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import { Customer, FormError, Payment, UserSessionType } from '../types'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { baseButtonWarning, baseButtonWhiteBg, textFieldWhiteBg } from '../theme/custom-styles'
import { getPaymentsWithSamePaymentReference, getPaymentWithAlertsDetailsData } from '../api/app-data.api'
import { getCustomersDataWithSameBankAccount, getCustomersDataWithSameDocumentNumber } from '../api/app-queries.api'
import { BankAccountNoveltyForm } from '../components/payment-to-review-details-management/bank-account-novelty-form'
import { checkIfPaymentIsTotallyReviewed, handleCheckIfBankAccountNumberIsValid, normalizePaymentData } from '../utils'
import { PaymentAmountNoveltyForm } from '../components/payment-to-review-details-management/payment-amount-novelty-form'
import { PaymentReferenceNoveltyForm } from '../components/payment-to-review-details-management/payment-reference-novelty-form'
import { PaymentToReviewDetailsTable } from '../components/payment-to-review-details-management/payment-to-review-details-table'
import { BackgroundResearchNoveltyForm } from '../components/payment-to-review-details-management/background-research-novelty-form'
import { IdentificationDocumentNoveltyForm } from '../components/payment-to-review-details-management/identification-document-novelty-form'
import { useSocket } from '../hooks/use-socket'

const PaymentToReviewDetailsManagement = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const { banksData } = useContext(AppStateContext)
  const { customerCompanyId, paymentId } = useParams()
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { activateAuth, isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['payments-to-review-management', 'common'])

  const alreadyRedirectedRef = useRef<boolean>(false)
  const approvingPaymentRef = useRef<boolean>(false)
  const rejectingPaymentRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const paymentDataUpdatedRef = useRef<boolean>(false)
  const bankAccountNoveltyFormRef = useRef<ReactNode | null>(null)
  const paymentAmountNoveltyFormRef = useRef<ReactNode | null>(null)
  const paymentReferenceNoveltyFormRef = useRef<ReactNode | null>(null)
  const currentPaymentDetailsRef = useRef<Payment | Partial<Payment>>({})
  const identificationDocumentNoveltyFormRef = useRef<ReactNode | null>(null)

  const [errors, setErrors] = useState<FormError>({})
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentActionReason, setPaymentActionReason] = useState<string>('')
  const [selectedNoveltyType, setSelectedNoveltyType] = useState<string>('')
  const [hideActionsButtons, setHideActionsButtons] = useState<boolean>(false)
  const [paymentData, setPaymentData] = useState<Payment | Partial<Payment> | null>(null)
  const [paymentIsCompleteReviewed, setPaymentIsCompleteReviewed] = useState<boolean>(false)
  const [dialogDefineNoveltyIsOpen, setDialogDefineNoveltyIsOpen] = useState<boolean>(false)
  const [dialogDefineNoveltyIsLoading, setDialogDefineNoveltyIsLoading] = useState<boolean>(false)
  const [paymentCustomerData, setPaymentCustomerData] = useState<Customer | Partial<Customer>>({})
  const [dialogConfirmDeletePaymentIsOpen, setDialogConfirmDeletePaymentIsOpen] = useState<boolean>(false)
  const [dialogConfirmRejectPaymentIsOpen, setDialogConfirmRejectPaymentIsOpen] = useState<boolean>(false)
  const [customerDataSuggested, setCustomerDataSuggested] = useState<Customer | Partial<Customer> | null>(null)
  const [dialogConfirmRejectPaymentIsLoading, setDialogConfirmRejectPaymentIsLoading] = useState<boolean>(false)
  const [dialogConfirmDeletePaymentIsLoading, setDialogConfirmDeletePaymentIsLoading] = useState<boolean>(false)
  const [dialogSetBackgroundResearchReasonIsOpen, setDialogSetBackgroundResearchReasonIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`updatePaymentToReview:${paymentId}`, async () => {
      handleGetPaymentToReviewDetailsFromRemote()
    })

    appDataSocket.on(`deletePaymentToReview:${paymentId}`, async () => {
      if (alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true
        navigate('/')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPaymentToReviewDetails()

    if (config && userSession && userSession.role.indexOf('super-admin') === -1 && config.environment === 'production' && config.app.scienciatechCompanyId === userSession.belongsTo) {
      setHideActionsButtons(true)
    }

    if (
      config &&
      userSession &&
      userSession.role.indexOf('super-admin') === -1 &&
      config.environment !== 'production' &&
      (config.app.scienciatechCompanyId === userSession.belongsTo || config.app.demoScienciatechCompanyId === userSession.belongsTo)
    ) {
      setHideActionsButtons(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (paymentData && !customerCompanyId) {
      handleRedirectIfPaymentIsTotallyReviewed(paymentData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData])

  const handleGetPaymentToReviewDetails = async () => {
    let localPaymentToReviewDetailsToSet: any = {}
    const localPaymentToReviewDetails = globalThis.sessionStorage.getItem(`paymentToReviewDetailsData:${paymentId}`)

    if (localPaymentToReviewDetails) {
      localPaymentToReviewDetailsToSet = JSON.parse(localPaymentToReviewDetails)
      handleSetInitialData(localPaymentToReviewDetailsToSet)
    }

    handleGetPaymentToReviewDetailsFromRemote()
  }

  const handleGetPaymentToReviewDetailsFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentWithAlertsDetailsData(String(paymentId), accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`paymentToReviewDetailsData:${paymentId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentToReviewDetailsDetails > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    if (Array.isArray(dataToSet.paymentWithAlerts) && dataToSet.paymentWithAlerts.length === 0) {
      if (alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true
        navigate('/', { replace: true })
      }
      return
    }

    if (Array.isArray(dataToSet.paymentWithAlerts)) {
      const paymentNormalizedData = normalizePaymentData(dataToSet.paymentWithAlerts) as Payment

      if (handleRedirectIfPaymentIsTotallyReviewed(paymentNormalizedData)) {
        return
      }

      if (!paymentNormalizedData.documentId) {
        if (
          alreadyRedirectedRef.current === false &&
          (paymentNormalizedData.status === 'reviewed' || paymentNormalizedData.status === 'register-disperse-completed' || paymentNormalizedData.status === 'dispersing' || paymentNormalizedData.status === 'paid')
        ) {
          alreadyRedirectedRef.current = true
          navigate('/payments-to-review', { replace: true })
          return
        }

        if (alreadyRedirectedRef.current === false && paymentNormalizedData.status !== 'waiting-for-resolution' && !customerCompanyId) {
          alreadyRedirectedRef.current = true
          navigate('/payments-to-review', { replace: true })
          return
        }
      }

      if (alreadyRedirectedRef.current === false && paymentNormalizedData.documentId && paymentNormalizedData.status !== 'waiting-for-resolution') {
        alreadyRedirectedRef.current = true
        navigate(`/document-novelties-details/${paymentNormalizedData.documentId}`, { replace: true })
        return
      }
    }

    const paymentNormalizedData = normalizePaymentData(dataToSet.paymentWithAlerts || {})
    currentPaymentDetailsRef.current = paymentNormalizedData

    if (paymentNormalizedData.status === 'rejected' || paymentNormalizedData.status === 'returned') {
      setHideActionsButtons(true)
    }

    setPaymentCustomerData(dataToSet.customerData)
    setPaymentData(dataToSet.paymentWithAlerts)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    rejectingPaymentRef.current = false
    approvingPaymentRef.current = false

    if (paymentData && !customerCompanyId) {
      handleCheckIfIsPaymentCompleteReviewed(paymentNormalizedData)
    }

    dataFullyLoadedRef.current = true
  }

  const handleRedirectIfPaymentIsTotallyReviewed = (paymentData: Payment | Partial<Payment> | null) => {
    const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentData)

    if (paymentData && paymentData.status !== 'waiting-for-resolution' && paymentData.status !== 'rejected' && paymentData.status !== 'returned') {
      if (paymentIsTotallyReviewed && paymentDataUpdatedRef.current === false && alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true

        if (paymentData?.documentId) {
          navigate(`/document-novelties-details/${paymentData?.documentId}`)
        } else {
          navigate('/payments-to-review')
        }

        return true
      }

      return false
    }

    return false
  }

  const handleGetPaymentReferenceToSet = (paymentReference: string) => {
    if (userSession?.role.includes('-client')) {
      return `${String(userSession.belongsTo).substr(String(userSession.belongsTo).length - 6)}-${String(paymentReference).trim()}`
    }

    return paymentReference.trim()
  }

  const handleGetInfoDataBelongsTo = () => {
    if (userSession?.role.includes('-client')) {
      return userSession.belongsTo
    }

    return 'holdings'
  }

  const handleCheckIfIsPaymentCompleteReviewed = (paymentData: Payment | Partial<Payment>) => {
    const totalOfPaymentNoveltiesResolved = paymentData.novelties?.filter((noveltyData) => noveltyData.resolved === true)

    if (Number(totalOfPaymentNoveltiesResolved?.length) === Number(paymentData.novelties?.length) && !paymentData.documentId && paymentData.status !== 'waiting-for-resolution') {
      setPaymentIsCompleteReviewed(true)
    }

    if (Number(totalOfPaymentNoveltiesResolved?.length) === Number(paymentData.novelties?.length) && paymentData.documentId && alreadyRedirectedRef.current === false) {
      alreadyRedirectedRef.current = true
      navigate(`/document-novelties-details/${paymentData.documentId}`)
    }
  }

  const handleDeletePayment = async () => {
    try {
      setErrors({})
      rejectingPaymentRef.current = true
      setDialogConfirmDeletePaymentIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await deletePaymentData(String(paymentData?._id), accessTokenData.data.accessToken)

      if (alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true

        updateNewNotificationStatus?.(true, 'payment-deleted-success')
        navigate('/payments-rejected')
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      rejectingPaymentRef.current = false
      setDialogConfirmRejectPaymentIsLoading(false)
    }
  }

  const handleRejectPayment = async () => {
    try {
      setErrors({})
      const paymentHasBackgroundResearchNovelty = paymentData?.novelties?.find((noveltyData) => noveltyData.type === 'background-research')

      if (paymentHasBackgroundResearchNovelty && !dialogSetBackgroundResearchReasonIsOpen) {
        setDialogSetBackgroundResearchReasonIsOpen(true)
        return
      }

      if (paymentHasBackgroundResearchNovelty && dialogSetBackgroundResearchReasonIsOpen && !paymentActionReason) {
        setErrors({
          paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField')
        })
        return
      }

      rejectingPaymentRef.current = true
      setDialogConfirmRejectPaymentIsLoading(true)
      setDialogSetBackgroundResearchReasonIsOpen(false)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await rejectPaymentData(String(paymentData?._id), { paymentActionReason }, accessTokenData.data.accessToken)

      updateNewNotificationStatus?.(true, 'payment-rejected-success')

      if (alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true

        if (paymentData?.documentId) {
          navigate(`/document-novelties-details/${paymentData?.documentId}`)
        } else {
          navigate('/payments-to-review')
        }
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      rejectingPaymentRef.current = false
      setDialogConfirmRejectPaymentIsLoading(false)
    }
  }

  const handleOpenDialogToResolveNovelty = (noveltyType: string) => {
    setSelectedNoveltyType(noveltyType)
    setTimeout(() => setDialogDefineNoveltyIsOpen(true), 100)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  const renderNoveltyToResolve = () => {
    switch (selectedNoveltyType) {
      case 'bank-account':
        return (
          <BankAccountNoveltyForm
            errors={errors}
            banksData={banksData}
            paymentData={paymentData}
            ref={bankAccountNoveltyFormRef}
            paymentCustomerData={paymentCustomerData}
            handleResolveSelectedNovelty={handleResolveSelectedNovelty}
          />
        )
      case 'payment-reference':
        return (
          <PaymentReferenceNoveltyForm
            errors={errors}
            paymentData={paymentData}
            userSession={userSession as UserSessionType}
            ref={paymentReferenceNoveltyFormRef}
          />
        )
      case 'identification-document':
        return (
          <IdentificationDocumentNoveltyForm
            errors={errors}
            paymentData={paymentData}
            ref={identificationDocumentNoveltyFormRef}
            customerDataSuggested={customerDataSuggested}
            handleClearCustomerDataSuggested={() => {
              setHideActionsButtons(false)
              setCustomerDataSuggested(null)
            }}
          />
        )
      case 'background-research':
        return <BackgroundResearchNoveltyForm paymentData={paymentData} />
      case 'payment-amount':
        return (
          <PaymentAmountNoveltyForm
            errors={errors}
            paymentData={paymentData}
            ref={paymentAmountNoveltyFormRef}
          />
        )
    }
  }

  const handleResolveSelectedNovelty = async () => {
    let formDataIsValid = true
    const formErrors: FormError = {}
    // eslint-disable-next-line no-useless-escape
    const regex = /[!@#$%^&*()\-+={}[\]:;"'<>,.?\/|\\]/

    switch (selectedNoveltyType) {
      case 'bank-account': {
        setErrors({})
        const { selectedBankAccountData, acceptDuplicateAnyway, acceptBankAccountNumberAnyway } = bankAccountNoveltyFormRef.current as unknown as {
          acceptDuplicateAnyway: boolean
          acceptBankAccountNumberAnyway: boolean
          selectedBankAccountData: { bankAchCode: string; bankName: string; bankAccountType: string; bankNumber: string }
        }

        if (!selectedBankAccountData.bankAchCode || !selectedBankAccountData.bankName) {
          formDataIsValid = false
          formErrors.bankName = t('bankAccountNoveltyForm.errorMessages.dataMandatory')
        }

        if (selectedBankAccountData.bankAchCode === 'undefined' || selectedBankAccountData.bankName === 'undefined') {
          formDataIsValid = false
          formErrors.bankName = t('bankAccountNoveltyForm.errorMessages.dataMandatory')
        }

        if (!selectedBankAccountData.bankAccountType) {
          formDataIsValid = false
          formErrors.bankAccountType = t('bankAccountNoveltyForm.errorMessages.dataMandatory')
        }

        if (!selectedBankAccountData.bankNumber.trim()) {
          formDataIsValid = false
          formErrors.bankNumber = t('bankAccountNoveltyForm.errorMessages.dataMandatory')
        }

        if (selectedBankAccountData.bankNumber && isNaN(Number(selectedBankAccountData.bankNumber.trim()))) {
          formDataIsValid = false
          formErrors.bankNumber = t('bankAccountNoveltyForm.errorMessages.hasToBeOnlyDigits')
        }

        if (!formDataIsValid) {
          return setErrors(formErrors)
        }

        setDialogDefineNoveltyIsLoading(true)
        const bankAccountNumberIsInvalid = handleCheckIfBankAccountNumberIsValid(selectedBankAccountData, selectedBankAccountData.bankNumber.trim(), t)

        if (bankAccountNumberIsInvalid) {
          formDataIsValid = false
          formErrors.bankAccountNumberLength = true
          formErrors.bankNumber = bankAccountNumberIsInvalid
        }

        if (!formDataIsValid && acceptBankAccountNumberAnyway === false) {
          setDialogDefineNoveltyIsLoading(false)
          return setErrors(formErrors)
        }

        if (acceptBankAccountNumberAnyway === true) {
          formDataIsValid = true // --> Esto lo hacemos para empezar las otras validaciones
        }

        const {
          data: { accessToken }
        } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        const { data } = await getCustomersDataWithSameBankAccount(String(userSession?.belongsTo), String(paymentCustomerData._id), String(selectedBankAccountData.bankNumber.trim()), accessToken)

        if (data > 0) {
          formDataIsValid = false
          formErrors.bankAccountDuplicated = true
          formErrors.bankNumber = t('bankAccountNoveltyForm.errorMessages.duplicateBankAccount', { ns: 'payments-to-review-management' })
        }

        if (!formDataIsValid && acceptDuplicateAnyway === false) {
          setDialogDefineNoveltyIsLoading(false)
          return setErrors(formErrors)
        }

        try {
          approvingPaymentRef.current = true
          const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          activateAuth?.({ token: accessTokenData.data.refreshToken ?? '' }) // Refrescamos el Refresh Token

          const paymentUpdatedData = await approveBankAccountPaymentNovelty(String(paymentData?._id), selectedBankAccountData, accessTokenData.data.accessToken)
          updateNewNotificationStatus?.(true, 'payment-novelty-approved-success')
          paymentDataUpdatedRef.current = true
          const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentUpdatedData.data.paymentUpdatedData)
          console.log('paymentIsTotallyReviewed handleResolveSelectedNovelty bank-account', paymentIsTotallyReviewed)
          setTimeout(() => {
            paymentDataUpdatedRef.current = false
          }, 1500)

          if (paymentIsTotallyReviewed && alreadyRedirectedRef.current === false) {
            alreadyRedirectedRef.current = true

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
            } else {
              navigate('/payments-to-review')
            }
          } else {
            setDialogDefineNoveltyIsOpen(false)
            setDialogDefineNoveltyIsLoading(false)
          }
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else if (error?.response?.status === 404) {
            setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          approvingPaymentRef.current = false
          setDialogDefineNoveltyIsLoading(false)
        }

        break
      }
      case 'payment-reference': {
        setErrors({})
        const { newPaymentReference } = paymentReferenceNoveltyFormRef.current as unknown as { [key: string]: string }

        if (!newPaymentReference) {
          formDataIsValid = false
          formErrors.errorPaymentReference = t('paymentReferenceNoveltyForm.errorMessages.dataMandatory')
        }

        if (newPaymentReference && newPaymentReference === 'null') {
          formDataIsValid = false
          formErrors.errorPaymentReference = t('paymentReferenceNoveltyForm.errorMessages.dataMandatory')
        }

        if (newPaymentReference && regex.test(newPaymentReference)) {
          formDataIsValid = false
          formErrors.registeredFullName = t('paymentReferenceNoveltyForm.errorMessages.dataWithSpecialChars')
        }

        if (newPaymentReference && newPaymentReference.indexOf(' ') > -1) {
          formDataIsValid = false
          formErrors.registeredFullName = t('paymentReferenceNoveltyForm.errorMessages.dataWithSpecialChars')
        }

        if (!formDataIsValid) {
          return setErrors(formErrors)
        }

        setDialogDefineNoveltyIsLoading(true)
        const {
          data: { accessToken }
        } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        const {
          data: { paymentsBySamePaymentReference }
        } = await getPaymentsWithSamePaymentReference(String(paymentData?._id), newPaymentReference.trim(), accessToken)

        if (Array.isArray(paymentsBySamePaymentReference) && paymentsBySamePaymentReference.length > 0) {
          setDialogDefineNoveltyIsLoading(false)
          return setErrors({
            errorPaymentReference: t('paymentReferenceNoveltyForm.errorMessages.duplicatePaymentReference')
          })
        }

        try {
          approvingPaymentRef.current = true
          const paymentReferenceToSet = handleGetPaymentReferenceToSet(newPaymentReference)
          const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          activateAuth?.({ token: accessTokenData.data.refreshToken ?? '' }) // Refrescamos el Refresh Token

          const paymentUpdatedData = await approvePaymentReferenceNovelty(String(paymentData?._id), paymentReferenceToSet, accessTokenData.data.accessToken)
          updateNewNotificationStatus?.(true, 'payment-novelty-approved-success')
          paymentDataUpdatedRef.current = true
          const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentUpdatedData.data.paymentUpdatedData)
          console.log('paymentIsTotallyReviewed handleResolveSelectedNovelty payment-reference', paymentIsTotallyReviewed)
          setTimeout(() => {
            paymentDataUpdatedRef.current = false
          }, 1500)

          if (paymentIsTotallyReviewed && alreadyRedirectedRef.current === false) {
            alreadyRedirectedRef.current = true

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
            } else {
              navigate('/payments-to-review')
            }
          } else {
            setDialogDefineNoveltyIsOpen(false)
            setDialogDefineNoveltyIsLoading(false)
          }
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else if (error?.response?.status === 404) {
            setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          approvingPaymentRef.current = false
          setDialogDefineNoveltyIsLoading(false)
        }

        break
      }
      case 'identification-document': {
        setErrors({})
        setHideActionsButtons(false)
        setDialogDefineNoveltyIsLoading(true)
        const { newCustomerData, unidentifiedCustomerId, customerSuggestionAccepted, customerId } = identificationDocumentNoveltyFormRef.current as unknown as {
          [key: string]: { registeredFullName: string; documentType: string; documentNumber: string }
        }

        if (!newCustomerData.registeredFullName) {
          formDataIsValid = false
          formErrors.registeredFullName = t('identificationDocumentNoveltyForm.errorMessages.dataMandatory')
        }

        if (!newCustomerData.documentType) {
          formDataIsValid = false
          formErrors.documentType = t('identificationDocumentNoveltyForm.errorMessages.dataMandatory')
        }

        if (!newCustomerData.documentNumber) {
          formDataIsValid = false
          formErrors.documentNumber = t('identificationDocumentNoveltyForm.errorMessages.dataMandatory')
        }

        if (newCustomerData.documentNumber && String(newCustomerData.documentNumber).length < 6) {
          formDataIsValid = false
          formErrors.documentNumber = t('identificationDocumentNoveltyForm.errorMessages.documentNumberWithLessCharacters')
        }

        if (newCustomerData.registeredFullName && regex.test(newCustomerData.registeredFullName)) {
          formDataIsValid = false
          formErrors.registeredFullName = t('identificationDocumentNoveltyForm.errorMessages.registeredFullNameError')
        }

        if (!formDataIsValid) {
          setDialogDefineNoveltyIsLoading(false)
          return setErrors(formErrors)
        }

        const {
          data: { accessToken }
        } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        const {
          data: { customersBySameDocumentNumber }
        } = await getCustomersDataWithSameDocumentNumber(newCustomerData.documentNumber, accessToken)

        if (Array.isArray(customersBySameDocumentNumber) && customersBySameDocumentNumber.length > 0) {
          if (
            (customersBySameDocumentNumber[0].documentNumber === newCustomerData.documentNumber &&
              customersBySameDocumentNumber[0].registeredFullName !== newCustomerData.registeredFullName &&
              !customerSuggestionAccepted) ||
            (customersBySameDocumentNumber[0].documentNumber !== newCustomerData.documentNumber &&
              customersBySameDocumentNumber[0].registeredFullName === newCustomerData.registeredFullName &&
              !customerSuggestionAccepted)
          ) {
            setCustomerDataSuggested({
              _id: String(customersBySameDocumentNumber[0]._id),
              documentType: customersBySameDocumentNumber[0].documentType,
              documentNumber: customersBySameDocumentNumber[0].documentNumber,
              registeredFullName: customersBySameDocumentNumber[0].registeredFullName
            })

            setDialogDefineNoveltyIsLoading(false)
            return setHideActionsButtons(true)
          }
        }

        try {
          approvingPaymentRef.current = true
          const dataBelongsTo = handleGetInfoDataBelongsTo()
          const customerDataToSet = { ...newCustomerData, customerId, dataBelongsTo, unidentifiedCustomerId }
          const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          activateAuth?.({ token: accessTokenData.data.refreshToken ?? '' }) // Refrescamos el Refresh Token

          const paymentUpdatedData = await approveIdentificationDocumentNovelty(String(paymentData?._id), customerDataToSet, accessTokenData.data.accessToken)
          updateNewNotificationStatus?.(true, 'payment-novelty-approved-success')
          paymentDataUpdatedRef.current = true
          const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentUpdatedData.data.paymentUpdatedData)
          console.log('paymentIsTotallyReviewed handleResolveSelectedNovelty identification-document', paymentIsTotallyReviewed)
          setTimeout(() => {
            paymentDataUpdatedRef.current = false
          }, 1500)

          if (paymentIsTotallyReviewed && alreadyRedirectedRef.current === false) {
            alreadyRedirectedRef.current = true

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
            } else {
              navigate('/payments-to-review')
            }
          } else {
            setDialogDefineNoveltyIsOpen(false)
            setDialogDefineNoveltyIsLoading(false)
          }
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else if (error?.response?.status === 404) {
            setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          approvingPaymentRef.current = false
          setDialogDefineNoveltyIsLoading(false)
        }

        break
      }
      case 'background-research': {
        setErrors({})

        if (!dialogSetBackgroundResearchReasonIsOpen) {
          setDialogSetBackgroundResearchReasonIsOpen(true)
          return
        }

        if (!paymentActionReason) {
          setErrors({
            paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField')
          })
          return
        }

        setDialogDefineNoveltyIsLoading(true)
        setDialogSetBackgroundResearchReasonIsOpen(false)

        try {
          approvingPaymentRef.current = true
          const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          activateAuth?.({ token: accessTokenData.data.refreshToken ?? '' }) // Refrescamos el Refresh Token

          const paymentUpdatedData = await approveBackgroundResearchNovelty(String(paymentData?._id), { paymentActionReason }, accessTokenData.data.accessToken)
          updateNewNotificationStatus?.(true, 'payment-novelty-approved-success')
          paymentDataUpdatedRef.current = true
          const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentUpdatedData.data.paymentUpdatedData)
          console.log('paymentIsTotallyReviewed handleResolveSelectedNovelty background-research', paymentIsTotallyReviewed)
          setTimeout(() => {
            paymentDataUpdatedRef.current = false
          }, 1500)

          if (paymentIsTotallyReviewed && alreadyRedirectedRef.current === false) {
            alreadyRedirectedRef.current = true

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
            } else {
              navigate('/payments-to-review')
            }
          } else {
            setDialogDefineNoveltyIsOpen(false)
            setDialogDefineNoveltyIsLoading(false)
          }
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else if (error?.response?.status === 404) {
            setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          approvingPaymentRef.current = false
          setDialogDefineNoveltyIsLoading(false)
        }

        break
      }
      case 'payment-amount': {
        setErrors({})
        const { newPaymentAmount } = paymentAmountNoveltyFormRef.current as unknown as { [key: string]: '' }
        const newPaymentAmountExtractedNumber = Number(newPaymentAmount.slice(1).trim().replaceAll(',', ''))

        if (!newPaymentAmount) {
          formDataIsValid = false
          formErrors.errorPaymentAmount = t('paymentAmountNoveltyForm.errorMessages.dataMandatory')
        }

        if (newPaymentAmount && isNaN(newPaymentAmountExtractedNumber)) {
          formDataIsValid = false
          formErrors.errorPaymentAmount = t('paymentAmountNoveltyForm.errorMessages.dataValidNumber')
        }

        if (newPaymentAmount && !isNaN(newPaymentAmountExtractedNumber) && newPaymentAmountExtractedNumber <= 0) {
          formDataIsValid = false
          formErrors.errorPaymentAmount = t('paymentAmountNoveltyForm.errorMessages.dataNotZero')
        }

        if (!formDataIsValid) {
          return setErrors(formErrors)
        }

        setDialogDefineNoveltyIsLoading(true)

        try {
          approvingPaymentRef.current = true
          const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          activateAuth?.({ token: accessTokenData.data.refreshToken ?? '' }) // Refrescamos el Refresh Token

          const paymentUpdatedData = await approvePaymentAmountNovelty(String(paymentData?._id), newPaymentAmountExtractedNumber, accessTokenData.data.accessToken)
          updateNewNotificationStatus?.(true, 'payment-novelty-approved-success')
          paymentDataUpdatedRef.current = true
          const paymentIsTotallyReviewed = checkIfPaymentIsTotallyReviewed(paymentUpdatedData.data.paymentUpdatedData)
          console.log('paymentIsTotallyReviewed handleResolveSelectedNovelty payment-amount', paymentIsTotallyReviewed)
          setTimeout(() => {
            paymentDataUpdatedRef.current = false
          }, 1500)

          if (paymentIsTotallyReviewed && alreadyRedirectedRef.current === false) {
            alreadyRedirectedRef.current = true

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
            } else {
              navigate('/payments-to-review')
            }
          } else {
            setDialogDefineNoveltyIsOpen(false)
            setDialogDefineNoveltyIsLoading(false)
          }
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else if (error?.response?.status === 404) {
            setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          approvingPaymentRef.current = false
          setDialogDefineNoveltyIsLoading(false)
        }

        break
      }
    }
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        {userSession &&
        userSession.role === 'super-admin-client' &&
        location.state?.previousPath &&
        (location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` || location.state?.previousPath === `customer-company-payments-history/${userSession.belongsTo}`) ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.home')}
            </Typography>
          </ListItemButton>
        ) : userSession && userSession.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === 'insights' ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/insights')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.insights')}
            </Typography>
          </ListItemButton>
        ) : location.state?.previousPath && location.state?.previousPath.indexOf('customer-company-details') > -1 ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(`/${location.state?.previousPath}`)}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.clientProfile')}
            </Typography>
          </ListItemButton>
        ) : (
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        )}
        <Typography
          key="3"
          style={{ cursor: 'pointer' }}
          className="breadcrumb-inactive"
          onClick={() => {
            if (userSession && userSession.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}`) {
              navigate(`/customer-company-payments/${userSession.belongsTo}`)
              return
            }

            if (userSession && userSession.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === 'insights') {
              navigate(`/payments-with-novelties/${userSession.belongsTo}`)
              return
            }

            if (location.state?.previousPath && location.state?.previousPath.indexOf('customer-company-details') > -1) {
              const customerCompanyId = location.state?.previousPath.split('/')[1]
              navigate(`/payments-with-novelties/${customerCompanyId}`)
              return
            }

            if (paymentData?.documentId) {
              navigate(`/document-novelties-details/${paymentData?.documentId}`)
              return
            }

            if (location.state?.previousPath && location.state?.previousPath === '/') {
              const customerCompanyId = String(userSession?.belongsTo) === 'holdings' ? String(paymentData?.belongsTo) : String(userSession?.belongsTo)
              navigate(`/payments-with-novelties/${customerCompanyId}`)
              return
            }

            if (location.state?.previousPath && location.state?.previousPath === `customer-company-payments-history/${userSession?.belongsTo}`) {
              const customerCompanyId = String(userSession?.belongsTo) === 'holdings' ? String(paymentData?.belongsTo) : String(userSession?.belongsTo)
              navigate(`/customer-company-payments-history/${customerCompanyId}`)
              return
            }

            navigate('/payments-to-review')
          }}
        >
          {userSession && userSession.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === `customer-company-payments/${userSession?.belongsTo}`
            ? t('breadcrumbs.individualPayments')
            : userSession && userSession.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === 'insights'
              ? t('breadcrumbs.paymentsWithNovelties')
              : (location.state?.previousPath && location.state?.previousPath.indexOf('customer-company-details') > -1) || (location.state?.previousPath && location.state?.previousPath === '/')
                ? t('breadcrumbs.paymentsWithNovelties')
                : location.state?.previousPath && location.state?.previousPath === `customer-company-payments-history/${userSession?.belongsTo}`
                  ? t('breadcrumbs.paymentsHistory')
                  : t('breadcrumbs.secondLevel')}
        </Typography>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.thirdLevel')}
        </Typography>
      </Breadcrumbs>
      {loadingLocalData ? (
        <div className="payment-to-review-details-container">
          <div className="payment-to-review-details-title">{t('title')}</div>
          <div className="documents-list-container">
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="payment-to-review-details-container">
            <div className="payment-to-review-details-title">{t('title')}</div>
            <PaymentToReviewDetailsTable
              paymentData={paymentData}
              paymentCustomerData={paymentCustomerData}
              hideReviewButtonInRow={!!customerCompanyId}
              handleOpenDialogToResolveNovelty={handleOpenDialogToResolveNovelty}
              showBackgroundNoveltiesData={paymentData?.status === 'waiting-for-resolution' || (paymentData?.status !== 'waiting-for-resolution' && userSession && userSession.belongsTo === 'holdings')}
            />
          </div>
          {userSession && userSession.belongsTo !== 'holdings' && (
            <div className="payment-to-review-details-action-buttons-container">
              {paymentIsCompleteReviewed && !paymentData?.documentId && (
                <div className="discart-payment-button-container">
                  <Button
                    variant="contained"
                    sx={baseButtonWhiteBg}
                    onClick={() => console.log('entro')}
                  >
                    {t('sendPayment')}
                  </Button>
                </div>
              )}
              {paymentData?.status === 'rejected' && (userSession?.role === 'super-admin' || userSession?.role === 'admin') && (
                <div className="discart-payment-button-container">
                  <Button
                    variant="contained"
                    sx={baseButtonWarning}
                    onClick={() => setDialogConfirmDeletePaymentIsOpen(true)}
                  >
                    {t('deletePayment')}
                  </Button>
                </div>
              )}
              {!paymentIsCompleteReviewed && paymentData?.status === 'for-review' && (
                <div className="discart-payment-button-container">
                  <Button
                    variant="contained"
                    sx={baseButtonWarning}
                    onClick={() => setDialogConfirmRejectPaymentIsOpen(true)}
                  >
                    {t('rejectPayment')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {dialogDefineNoveltyIsOpen && (
        <Dialog
          errors={errors}
          hideActionsButtons={hideActionsButtons}
          dialogTitle={String(paymentData?.status) === 'returned' || String(paymentData?.status) === 'rejected' ? t('defineNoveltyModal.titleReview') : t('defineNoveltyModal.title')}
          dialogIsLoading={dialogDefineNoveltyIsLoading}
          handleDialogAction={handleResolveSelectedNovelty}
          actionButtonText={t('defineNoveltyModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
            }

            setDialogDefineNoveltyIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">{renderNoveltyToResolve()}</div>
        </Dialog>
      )}
      {dialogConfirmRejectPaymentIsOpen && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={handleRejectPayment}
          dialogTitle={t('confirmRejectPaymentModal.title')}
          dialogIsLoading={dialogConfirmRejectPaymentIsLoading}
          actionButtonText={t('confirmRejectPaymentModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
            }

            setDialogConfirmRejectPaymentIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <div>{t('confirmRejectPaymentModal.instruction')}</div>
          </div>
        </Dialog>
      )}
      {dialogConfirmDeletePaymentIsOpen && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={handleDeletePayment}
          dialogTitle={t('confirmDeletePaymentModal.title')}
          dialogIsLoading={dialogConfirmDeletePaymentIsLoading}
          actionButtonText={t('confirmDeletePaymentModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
            }

            setDialogConfirmDeletePaymentIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <div>{t('confirmDeletePaymentModal.instruction')}</div>
          </div>
        </Dialog>
      )}
      {dialogSetBackgroundResearchReasonIsOpen && (
        <Dialog
          errors={errors}
          handleDialogAction={() => {
            if (dialogConfirmRejectPaymentIsOpen) {
              handleRejectPayment()
            } else {
              handleResolveSelectedNovelty()
            }
          }}
          dialogIsLoading={dialogConfirmDeletePaymentIsLoading}
          dialogTitle={t('confirmSetBackgroundResearchReasonModal.title')}
          actionButtonText={t('confirmSetBackgroundResearchReasonModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setPaymentActionReason('')
            }

            setDialogSetBackgroundResearchReasonIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <TextField
              fullWidth
              multiline
              variant="standard"
              sx={textFieldWhiteBg}
              id="filled-textarea"
              value={paymentActionReason || ''}
              error={!!errors.paymentActionReason}
              helperText={errors.paymentActionReason}
              onChange={(event) => {
                setErrors({})
                setPaymentActionReason(event.target.value.toUpperCase())
              }}
              label={t('confirmSetBackgroundResearchReasonModal.placeholder')}
            />
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default PaymentToReviewDetailsManagement
